import { Component } from '@theme/component';
import { ThemeEvents, WishlistUpdateEvent } from '@theme/events';

const STORAGE_KEY = 'yusufz-wishlist';

/**
 * Plain localStorage-backed wishlist store, shared by every button and the
 * listing page on the current view. No customer account or admin data is
 * involved -- this is a guest/per-browser wishlist, consistent with the
 * "no login required" approach already used for YUSUFZ Reading.
 */
export const WishlistStore = {
  /** @returns {string[]} */
  list() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  },

  /** @param {string} handle */
  has(handle) {
    return this.list().includes(handle);
  },

  /**
   * Adds or removes a handle and dispatches a WishlistUpdateEvent either way.
   *
   * @param {string} handle
   * @returns {boolean} True if the product is now saved, false if it was just removed.
   */
  toggle(handle) {
    const handles = this.list();
    const index = handles.indexOf(handle);
    const saved = index === -1;

    if (saved) {
      handles.push(handle);
    } else {
      handles.splice(index, 1);
    }

    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    } catch {
      // localStorage can be unavailable (private browsing, storage disabled); the toggle
      // still dispatches so on-page UI reflects the choice for the rest of this visit.
    }

    document.dispatchEvent(new WishlistUpdateEvent(handle, saved));

    return saved;
  },

  /** @param {string} handle */
  remove(handle) {
    if (this.has(handle)) this.toggle(handle);
  },
};

/**
 * A single wishlist toggle button for the product it's closest to (see
 * blocks/wishlist-button.liquid). Several instances of the same product's
 * button can exist on one page; each stays in sync with the others via the
 * shared wishlist:update event.
 *
 * @typedef {{ label: HTMLElement, toggleButton: HTMLElement }} WishlistButtonRefs
 * @extends {Component<WishlistButtonRefs>}
 */
export class WishlistButtonComponent extends Component {
  requiredRefs = ['label', 'toggleButton'];

  get #handle() {
    return this.dataset.productHandle || '';
  }

  connectedCallback() {
    super.connectedCallback();
    this.#render();
    document.addEventListener(ThemeEvents.wishlistUpdate, this.#onWishlistUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.wishlistUpdate, this.#onWishlistUpdate);
  }

  toggle() {
    if (!this.#handle) return;
    WishlistStore.toggle(this.#handle);
  }

  /** @param {Event} event */
  #onWishlistUpdate = (event) => {
    const { handle } = /** @type {CustomEvent<{handle: string}>} */ (event).detail;
    if (handle === this.#handle) this.#render();
  };

  #render() {
    const saved = this.#handle ? WishlistStore.has(this.#handle) : false;

    this.refs.toggleButton.setAttribute('aria-pressed', String(saved));
    this.refs.label.textContent = saved ? 'Saved to wishlist' : 'Add to wishlist';
  }
}

if (!customElements.get('wishlist-button-component')) {
  customElements.define('wishlist-button-component', WishlistButtonComponent);
}

/**
 * The "My Wishlist" listing page (see sections/wishlist.liquid). Fetches each
 * saved product's data from Shopify's native `/products/{handle}.js` JSON
 * endpoint -- no app or Admin API needed -- and renders a simple card grid.
 *
 * @typedef {{ empty: HTMLElement, grid: HTMLElement }} WishlistPageRefs
 * @extends {Component<WishlistPageRefs>}
 */
export class WishlistPageComponent extends Component {
  requiredRefs = ['empty', 'grid'];

  connectedCallback() {
    super.connectedCallback();
    this.#load();
    document.addEventListener(ThemeEvents.wishlistUpdate, this.#load);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.wishlistUpdate, this.#load);
  }

  #load = async () => {
    const handles = WishlistStore.list();

    if (handles.length === 0) {
      this.refs.empty.hidden = false;
      this.refs.grid.hidden = true;
      this.refs.grid.replaceChildren();
      return;
    }

    this.refs.empty.hidden = true;
    this.refs.grid.hidden = false;

    const products = await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(`/products/${handle}.js`);
          if (!response.ok) return null;
          return await response.json();
        } catch {
          return null;
        }
      })
    );

    this.refs.grid.replaceChildren(
      ...products.filter(Boolean).map((product) => this.#buildCard(/** @type {any} */ (product)))
    );
  };

  /** @param {any} product */
  #buildCard(product) {
    const item = document.createElement('li');
    item.className = 'wishlist-page__item';

    const link = document.createElement('a');
    link.href = product.url ?? '#';
    link.className = 'wishlist-page__item-link';

    if (product.featured_image) {
      const image = document.createElement('img');
      image.src = product.featured_image;
      image.alt = product.title ?? '';
      image.loading = 'lazy';
      image.className = 'wishlist-page__item-image';
      link.append(image);
    }

    const vendor = document.createElement('span');
    vendor.className = 'wishlist-page__item-vendor';
    vendor.textContent = product.vendor ?? '';

    const title = document.createElement('span');
    title.className = 'wishlist-page__item-title';
    title.textContent = product.title ?? '';

    const price = document.createElement('span');
    price.className = 'wishlist-page__item-price';
    price.textContent = typeof product.price === 'number' ? `$${(product.price / 100).toFixed(2)}` : '';

    link.append(vendor, title, price);

    const removeButton = document.createElement('button');
    removeButton.type = 'button';
    removeButton.className = 'button-unstyled wishlist-page__item-remove';
    removeButton.textContent = 'Remove';
    // Every row's button reads just "Remove" out of context, so a screen-reader
    // user listing the page's controls hears it repeated with nothing to tell
    // them apart. The visible text stays short; the accessible name names the
    // product (WCAG 2.4.4 / 4.1.2).
    removeButton.setAttribute('aria-label', `Remove ${product.title ?? 'item'} from wishlist`);
    removeButton.setAttribute('on:click', '/removeItem');
    removeButton.dataset.handle = product.handle ?? '';

    item.append(link, removeButton);

    return item;
  }

  /** @param {Event} event */
  removeItem(event) {
    const button = /** @type {HTMLElement} */ (event.target);
    const handle = button.dataset.handle;
    if (handle) WishlistStore.remove(handle);
  }
}

if (!customElements.get('wishlist-page-component')) {
  customElements.define('wishlist-page-component', WishlistPageComponent);
}
